/**
 * A ring map: circles drawn on four rings around a centre.
 *
 * The caller decides what a ring, a size and a colour mean, and passes each
 * circle ready to draw. The People tab draws the people of one stack this way,
 * and the Clients tab draws the stacks of a view.
 *
 * This file owns the geometry: where a circle sits, which names fit beside
 * the circles without running into each other, and the hover and select
 * behaviour. Colours come from the theme tokens in `Constellation.css`. The
 * one colour passed in is a circle's own fill.
 */
import { useMemo, useState, type KeyboardEvent } from 'react';
import './Constellation.css';

export type BadgeDirection = 'up' | 'down' | null;

/** One circle, ready to draw. */
export interface ConstellationPlanet {
  /** Stable identity for selection and React keys. */
  key: string;
  /** 0 is the innermost ring, 3 the outermost. A value out of range is not drawn. */
  ringIndex: number;
  /** Radius in SVG pixels. Use `scalePlanetRadius` from clientUserMap.ts. */
  sizeR: number;
  /** The circle's fill. */
  fillColor: string;
  /** An arrow on the circle: something moved up or down lately. */
  badge: BadgeDirection;
  /** A short name drawn beside the circle when there is room for it. */
  label?: string;
  /** Lines for the hover card. The first line is the title. */
  tooltipLines: string[];
}

interface ConstellationProps {
  planets: ConstellationPlanet[];
  /** Ring names, innermost first. */
  ringLabels: readonly [string, string, string, string];
  /** The name inside the centre disc. */
  centerLabel: string;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  /**
   * When set, only these circles stay at full strength and the rest fade, as
   * a search does. Positions do not change. Null or absent: no fade.
   */
  highlightKeys?: ReadonlySet<string> | null;
}

// ─── geometry ───────────────────────────────────────────────────────────

const VIEWBOX_W = 900;
const VIEWBOX_H = 700;
const CX = VIEWBOX_W / 2;
const CY = VIEWBOX_H / 2;

/** The radius each ring's circles sit on. */
const RING_RADII = [100, 170, 235, 295] as const;
/** The guide lines between the rings. */
const RING_BOUNDARIES = [70, 135, 200, 265, 320] as const;

const CENTRE_RADIUS = 38;

// ─── where each circle sits ─────────────────────────────────────────────

interface PlacedPlanet extends ConstellationPlanet {
  x: number;
  y: number;
  /** Angle from the centre. Decides which side of the circle its name sits on. */
  angle: number;
}

/**
 * A crowded ring is split into two or three lanes, so neighbours do not sit on
 * top of each other. The offsets keep every lane inside the ring's own band.
 */
const LANE_OFFSETS: readonly (readonly number[])[] = [[0], [-11, 11], [-18, 0, 18]];

function lanesFor(radius: number, count: number): number {
  const spacing = (2 * Math.PI * radius) / Math.max(1, count);
  if (spacing >= 28) return 1;
  if (spacing >= 14) return 2;
  return 3;
}

function placePlanets(planets: ConstellationPlanet[]): PlacedPlanet[] {
  const buckets: ConstellationPlanet[][] = RING_RADII.map(() => []);
  for (const p of planets) {
    if (p.ringIndex < 0 || p.ringIndex >= buckets.length) continue;
    buckets[p.ringIndex].push(p);
  }
  // Sorted by key, so a circle keeps its place from one draw to the next.
  for (const b of buckets) b.sort((a, c) => a.key.localeCompare(c.key));

  const out: PlacedPlanet[] = [];
  buckets.forEach((bucket, ringIdx) => {
    const n = bucket.length;
    const offsets = LANE_OFFSETS[lanesFor(RING_RADII[ringIdx], n) - 1];
    bucket.forEach((p, i) => {
      // Start at the top and go round evenly.
      const angle = -Math.PI / 2 + ((i + 0.5) / n) * Math.PI * 2;
      const radius = RING_RADII[ringIdx] + offsets[i % offsets.length];
      out.push({ ...p, x: CX + Math.cos(angle) * radius, y: CY + Math.sin(angle) * radius, angle });
    });
  });
  return out;
}

// ─── the arrow on a circle ──────────────────────────────────────────────

const BADGE_MAX_R = 7;

/**
 * Where a circle's arrow sits, from the circle's centre, and how big it is. A
 * small circle gets a small arrow, so the arrow never hides the circle.
 */
function badgeGeometry(planetR: number): { dx: number; dy: number; r: number } {
  const offset = planetR * 0.7;
  return { dx: offset, dy: -offset, r: Math.max(4, Math.min(BADGE_MAX_R, planetR * 0.6)) };
}

// ─── which names fit ────────────────────────────────────────────────────

const LABEL_MAX_CHARS = 16;
/** Width of one character of a 9px name, measured generously. */
const LABEL_CHAR_W = 5.2;
const LABEL_H = 11;
const LABEL_GAP = 4;

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface PlacedLabel {
  key: string;
  text: string;
  x: number;
  y: number;
  anchor: 'start' | 'end';
}

const overlaps = (a: Box, b: Box): boolean => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;

const shortLabel = (label: string): string =>
  label.length > LABEL_MAX_CHARS ? `${label.slice(0, LABEL_MAX_CHARS - 1)}…` : label;

/** The room the ring names take, straight above the centre. */
function ringLabelBoxes(ringLabels: readonly string[]): Box[] {
  return ringLabels.map((text, i) => {
    const { y, w, h } = ringLabelGeometry(text, i);
    return { x0: CX - w / 2, y0: y - h / 2, x1: CX + w / 2, y1: y + h / 2 };
  });
}

function ringLabelGeometry(text: string, i: number): { y: number; w: number; h: number } {
  const r = i < RING_BOUNDARIES.length - 1 ? RING_BOUNDARIES[i + 1] : RING_BOUNDARIES[RING_BOUNDARIES.length - 1] + 15;
  return { y: CY - r, w: text.length * 6.8 + 18, h: 18 };
}

/**
 * Pick the names that can be drawn without running into a circle, another
 * name, a ring name or the centre. Names are tried in the order given by
 * `first` (the selected circle, then a search's matches), then largest circle
 * first. A name that does not fit is left out: it is still on the hover card.
 */
function placeLabels(
  placed: PlacedPlanet[],
  ringLabels: readonly string[],
  first: (key: string) => number
): PlacedLabel[] {
  const blocked: Box[] = [
    ...ringLabelBoxes(ringLabels),
    { x0: CX - CENTRE_RADIUS, y0: CY - CENTRE_RADIUS, x1: CX + CENTRE_RADIUS, y1: CY + CENTRE_RADIUS },
  ];
  // A circle's room includes its arrow, which sticks out at the top right.
  const circles = placed.map((p) => {
    const box: Box = { x0: p.x - p.sizeR, y0: p.y - p.sizeR, x1: p.x + p.sizeR, y1: p.y + p.sizeR };
    if (p.badge) {
      const b = badgeGeometry(p.sizeR);
      box.x1 = Math.max(box.x1, p.x + b.dx + b.r + 1);
      box.y0 = Math.min(box.y0, p.y + b.dy - b.r - 1);
    }
    return { key: p.key, box };
  });
  const order = placed
    .filter((p) => p.label)
    .sort((a, b) => first(a.key) - first(b.key) || b.sizeR - a.sizeR || a.key.localeCompare(b.key));

  const out: PlacedLabel[] = [];
  for (const p of order) {
    const text = shortLabel(p.label as string);
    const w = text.length * LABEL_CHAR_W;
    const onRight = Math.cos(p.angle) >= 0;
    // On the right, the name starts past the circle's own arrow when the arrow hangs low enough to meet it.
    let reach = p.sizeR;
    if (onRight && p.badge) {
      const b = badgeGeometry(p.sizeR);
      if (b.dy + b.r > -LABEL_H / 2) reach = Math.max(reach, b.dx + b.r + 1);
    }
    const x = onRight ? p.x + reach + LABEL_GAP : p.x - p.sizeR - LABEL_GAP;
    const box: Box = { x0: onRight ? x : x - w, y0: p.y - LABEL_H / 2, x1: onRight ? x + w : x, y1: p.y + LABEL_H / 2 };
    if (box.x0 < 2 || box.x1 > VIEWBOX_W - 2) continue;
    if (blocked.some((b) => overlaps(box, b))) continue;
    if (circles.some((c) => c.key !== p.key && overlaps(box, c.box))) continue;
    blocked.push(box);
    out.push({ key: p.key, text, x, y: p.y, anchor: onRight ? 'start' : 'end' });
  }
  return out;
}

// ─── the name in the centre ─────────────────────────────────────────────

const CENTRE_LINE_CHARS = 11;

/** A long name is broken at its hyphens and spaces into up to three short lines. */
function centreLines(label: string): string[] {
  // Each word keeps the hyphen or space that follows it.
  const words = label.match(/[^-_\s]+[-_\s]*/g) ?? [];
  const lines: string[] = [];
  for (const word of words) {
    const last = lines[lines.length - 1];
    if (last !== undefined && (last + word).trimEnd().length <= CENTRE_LINE_CHARS)
      lines[lines.length - 1] = last + word;
    else lines.push(word);
  }
  const fit = (line: string) => {
    const text = line.trim();
    return text.length > CENTRE_LINE_CHARS ? `${text.slice(0, CENTRE_LINE_CHARS - 1)}…` : text;
  };
  if (lines.length <= 3) return lines.map(fit);
  return [fit(lines[0]), fit(lines[1]), fit(`${lines[2].trim().slice(0, CENTRE_LINE_CHARS - 1)}…`)];
}

// ─── component ──────────────────────────────────────────────────────────

export function Constellation({
  planets,
  ringLabels,
  centerLabel,
  selectedKey,
  onSelect,
  highlightKeys,
}: ConstellationProps) {
  const placed = useMemo(() => placePlanets(planets), [planets]);
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const highlight = highlightKeys ?? null;
  const labels = useMemo(
    () => placeLabels(placed, ringLabels, (key) => (key === selectedKey ? 0 : highlight?.has(key) ? 1 : 2)),
    [placed, ringLabels, selectedKey, highlight]
  );

  const isolating = selectedKey !== null;
  const isDim = (key: string): boolean => (isolating ? key !== selectedKey : highlight !== null && !highlight.has(key));
  const hovered = hoverKey ? placed.find((p) => p.key === hoverKey) : undefined;

  const centre = centreLines(centerLabel);
  const longest = Math.max(0, ...centre.map((l) => l.length));
  const centreFont = longest <= 5 ? 15 : longest <= 8 ? 12 : 10;
  const centreLead = centreFont + 2;

  const onPlanetKey = (e: KeyboardEvent<SVGGElement>, key: string) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onSelect(key === selectedKey ? null : key);
  };

  return (
    <div className="constellation">
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        className="constellation__svg"
        preserveAspectRatio="xMidYMid meet"
        onClick={() => onSelect(null)}
      >
        {RING_BOUNDARIES.map((r) => (
          <circle key={r} cx={CX} cy={CY} r={r} className="constellation__ring" />
        ))}

        {placed.map((p) => {
          const isSelected = p.key === selectedKey;
          const cls = ['constellation__planet'];
          if (isSelected) cls.push('constellation__planet--selected');
          if (isDim(p.key)) cls.push('constellation__planet--dim');
          return (
            <g
              key={p.key}
              transform={`translate(${p.x}, ${p.y})`}
              className={cls.join(' ')}
              role="button"
              tabIndex={0}
              aria-label={p.tooltipLines[0] ?? p.label ?? p.key}
              aria-pressed={isSelected}
              onMouseEnter={() => setHoverKey(p.key)}
              onMouseLeave={() => setHoverKey(null)}
              onFocus={() => setHoverKey(p.key)}
              onBlur={() => setHoverKey(null)}
              onKeyDown={(e) => onPlanetKey(e, p.key)}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(isSelected ? null : p.key);
              }}
            >
              {isSelected && <circle r={p.sizeR + 8} stroke={p.fillColor} className="constellation__halo" />}
              <circle r={p.sizeR} fill={p.fillColor} className="constellation__body" />
              {p.badge && <ChevronBadge direction={p.badge} planetR={p.sizeR} />}
            </g>
          );
        })}

        {/* Names sit above every circle, so a neighbour never covers one. */}
        <g className="constellation__labels">
          {labels.map((l) => (
            <text
              key={l.key}
              x={l.x}
              y={l.y + 3.5}
              textAnchor={l.anchor}
              className={isDim(l.key) ? 'constellation__label constellation__label--dim' : 'constellation__label'}
            >
              {l.text}
            </text>
          ))}
        </g>

        {ringLabels.map((text, i) => {
          const { y, w, h } = ringLabelGeometry(text, i);
          return (
            <g
              key={i}
              className={
                isolating ? 'constellation__ring-name constellation__ring-name--dim' : 'constellation__ring-name'
              }
            >
              <rect x={CX - w / 2} y={y - h / 2} width={w} height={h} rx={h / 2} />
              <text x={CX} y={y + 3.5} textAnchor="middle">
                {text}
              </text>
            </g>
          );
        })}

        <g className="constellation__centre">
          <circle cx={CX} cy={CY} r={CENTRE_RADIUS} />
          {centre.map((line, i) => (
            <text
              key={i}
              x={CX}
              y={CY + centreFont * 0.35 + (i - (centre.length - 1) / 2) * centreLead}
              textAnchor="middle"
              fontSize={centreFont}
            >
              {line}
            </text>
          ))}
        </g>

        {hovered && hovered.tooltipLines.length > 0 && (
          <TooltipBlock x={hovered.x} y={hovered.y} planetR={hovered.sizeR} lines={hovered.tooltipLines} />
        )}
      </svg>
    </div>
  );
}

// ─── badge ──────────────────────────────────────────────────────────────

function ChevronBadge({ direction, planetR }: { direction: 'up' | 'down'; planetR: number }) {
  const { dx, dy, r } = badgeGeometry(planetR);
  const path = direction === 'up' ? 'M -3 1.5 L 0 -1.5 L 3 1.5' : 'M -3 -1.5 L 0 1.5 L 3 -1.5';
  return (
    <g
      transform={`translate(${dx}, ${dy}) scale(${r / BADGE_MAX_R})`}
      className={`constellation__badge constellation__badge--${direction}`}
    >
      <circle r={BADGE_MAX_R} />
      <path d={path} />
    </g>
  );
}

// ─── hover card ─────────────────────────────────────────────────────────

interface TooltipBlockProps {
  x: number;
  y: number;
  planetR: number;
  lines: string[];
}

function TooltipBlock({ x, y, planetR, lines }: TooltipBlockProps) {
  const maxChars = Math.max(...lines.map((l) => l.length));
  const w = Math.max(120, maxChars * 6.5 + 18);
  const h = lines.length * 16 + 14;
  // Beside the circle, on whichever side has the room, and never off the map.
  let bx = x + planetR + 12;
  if (bx + w > VIEWBOX_W - 8) bx = x - planetR - 12 - w;
  if (bx < 8) bx = 8;
  let by = y - h / 2;
  if (by < 8) by = 8;
  if (by + h > VIEWBOX_H - 8) by = VIEWBOX_H - 8 - h;

  return (
    <g transform={`translate(${bx}, ${by})`} className="constellation__tooltip">
      <rect x={0} y={0} width={w} height={h} rx={8} />
      {lines.map((line, i) => (
        <text key={i} x={10} y={20 + i * 16} className={i === 0 ? 'constellation__tooltip-title' : undefined}>
          {line}
        </text>
      ))}
    </g>
  );
}
